import { Component, OnInit, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';

import { IstDatePipe } from '../../pipes/ist-date.pipe';
import { AuthService } from '../../services/auth/auth.service';
import { AvatarService } from '../../services/design/avatar.service';
import { SnackbarService } from '../../services/design/snackbar.service';
import { ThemeService } from '../../services/design/theme.service';
import { NotificationService } from '../../services/notifications/notification.service';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { roleLabel } from '../../core/auth/roles';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [SidebarComponent, RouterModule, CommonModule, IstDatePipe],
  template: `
    <div class="flex h-screen w-full bg-base-200/60 overflow-hidden text-base-content">
      <app-sidebar [collapsed]="isSidebarCollapsed" [class.phone-picking-navigation]="isPickingPage()"></app-sidebar>

      <div class="flex min-w-0 flex-1 flex-col overflow-hidden">
        @if(auth.hasStoreOperations()) { <div class="flex gap-3 px-4 py-1 text-xs" [class.bg-warning]="notificationService.stale()" role="status">
          <span>{{ notificationService.connection() }}</span>
          <span>Last sync: {{ notificationService.lastSyncedAt() | istDate:'shortTime' }}</span>
          <span>Last event: {{ notificationService.lastEventAt() | istDate:'shortTime' }}</span>
          <a routerLink="/operations/needs-action">Needs action</a>
          <button (click)="notificationService.refresh()">Sync now</button>
        </div> }
        <header
          class="navbar sticky top-0 z-30 border-b border-base-300 bg-base-100/90 px-4 py-3 shadow-sm backdrop-blur lg:px-6"
        >
          <div class="flex min-w-0 flex-1 items-center gap-3">
            <button class="btn btn-ghost btn-circle" [class.phone-picking-navigation]="isPickingPage()" (click)="toggleSidebar()">
              <i class="fa-solid fa-bars text-lg"></i>
            </button>

            <div class="hidden min-w-0 lg:block">
              <h1 class="truncate text-lg font-semibold">{{ appName }}</h1>
            </div>
          </div>

          <div class="flex items-center gap-2 lg:gap-3">
            <div
              class="hidden items-center gap-2 rounded-2xl border border-base-300 bg-base-100 px-3 py-2 text-sm text-base-content/70 xl:flex"
            >
              <span
                class="inline-flex h-2.5 w-2.5 rounded-full bg-success"
              ></span>
              All systems normal
            </div>

            @if(auth.hasStoreOperations()) { <div class="dropdown dropdown-end">
              <div tabindex="0" class="btn btn-ghost btn-circle relative">
                <div class="indicator">
                  <i class="fa-regular fa-bell text-lg"></i>
                  @if (notificationService.unreadOrders().length > 0) {
                    <span class="badge badge-xs indicator-item border-none bg-error text-white animate-bounce">
                      {{ notificationService.unreadOrders().length }}
                    </span>
                  }
                </div>
              </div>

              <div
                tabindex="0"
                class="dropdown-content card card-compact mt-3 w-80 border border-base-300 bg-base-100 shadow-xl z-[100]"
              >
                <div class="card-body">
                  <div class="flex items-center justify-between">
                    <span class="font-black text-sm uppercase tracking-widest text-base-content/50">{{ notificationService.pendingTotal() }} unaccepted orders (oldest 50)</span>
                    @if (notificationService.unreadOrders().length > 0) {
                      <button (click)="notificationService.clearUnread()" class="text-[10px] font-black text-primary hover:underline uppercase">Clear All</button>
                    }
                  </div>
                  
                  <div class="mt-2 divide-y divide-base-200 max-h-96 overflow-y-auto">
                    @for (order of notificationService.unreadOrders(); track order.id) {
                      <button (click)="navigateToOrder()" class="w-full text-left py-3 cursor-pointer group hover:bg-base-200/50 -mx-1 px-3 rounded-xl transition-all">
                        <div class="flex gap-3">
                          <div class="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0 group-hover:scale-110 transition-transform">
                            <i class="fa-solid fa-cart-shopping text-sm"></i>
                          </div>
                          <div class="flex-1 min-w-0">
                            <p class="text-sm font-bold truncate">New Order #{{ order.order_number || order.id.slice(0,8) }}</p>
                            <p class="text-xs text-base-content/50">Placed at {{ order.created_at | istDate:'shortTime' }}</p>
                            <p class="text-[10px] font-black text-success mt-1">₹{{ order.total_amount | number }}</p>
                          </div>
                        </div>
                      </button>
                    } @empty {
                      <div class="py-12 text-center">
                        <i class="fa-solid fa-bell-slash text-base-content/10 text-4xl mb-3"></i>
                        <p class="text-sm font-bold text-base-content/30 uppercase tracking-widest">No new orders</p>
                      </div>
                    }
                  </div>
                  
                  @if (notificationService.unreadOrders().length > 0) {
                    <div class="card-actions mt-2 bg-base-200/50 -mx-4 -mb-4 p-3 border-t border-base-200">
                      <button routerLink="/orders" class="btn btn-primary btn-sm btn-block rounded-xl font-black uppercase text-xs">View All Orders</button>
                    </div>
                  }
                </div>
              </div>
            </div>

            } <div class="dropdown dropdown-end relative">
              <button
                tabindex="0"
                class="group flex items-center gap-3 rounded-full border border-base-300 bg-base-100 px-2 py-1.5 transition hover:bg-base-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <div class="avatar">
                  <div
                    class="h-9 w-9 overflow-hidden rounded-full ring-1 ring-base-300"
                  >
                    <img
                      [src]="avatarUrl || 'https://i.pravatar.cc/40'"
                      alt="User avatar"
                      class="h-full w-full object-cover"
                    />
                  </div>
                </div>

                <div class="hidden flex-col text-left leading-tight md:flex">
                  <span class="text-sm font-semibold text-base-content">
                    {{ this.loginUser$?.name || 'User' }}
                  </span>
                  <span class="text-xs text-base-content/50">
                    {{ assignedRoleLabels() }}
                  </span>
                </div>

                <svg
                  class="h-4 w-4 text-base-content/40 transition group-hover:text-base-content/60"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              <ul
                tabindex="0"
                class="dropdown-content menu menu-sm mt-2 w-52 rounded-lg border border-base-300 bg-base-100 shadow-lg"
              >
                <li>
                  <a
                    [routerLink]="'/settings/account-details'"
                    class="flex items-center gap-2 hover:bg-primary/10"
                  >
                    <span><i class="fa-regular fa-user"></i></span>
                    Profile
                  </a>
                </li>
                <li>
                  <a
                    [routerLink]="auth.hasStoreOperations()?'/settings':'/settings/account-details'"
                    class="flex items-center gap-2 hover:bg-primary/10"
                  >
                    <span><i class="fa-solid fa-gear"></i></span>
                    Settings
                  </a>
                </li>
                <li>
                  <button
                    (click)="logout()"
                    class="flex cursor-pointer items-center gap-2 hover:bg-primary/10"
                  >
                    <span><i class="fa-solid fa-right-from-bracket"></i></span>
                    Logout
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </header>

        <main class="flex-1 overflow-y-auto p-4 lg:p-6" [class.phone-picking-content]="isPickingPage()">
          <router-outlet></router-outlet>
        </main>
      </div>
    </div>
  `,
  styles: [`@media(max-width:640px){.phone-picking-navigation{display:none!important}.phone-picking-content{padding:0}}`],
})
export class LayoutComponent implements OnInit {
  readonly auth=inject(AuthService);
  readonly roleLabel=roleLabel;
  assignedRoleLabels() { return this.auth.roles().map(roleLabel).join(' · '); }
  readonly #authService = inject(AuthService);

  readonly avatarService = inject(AvatarService);
  readonly themeService = inject(ThemeService);
  readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly snackbar = inject(SnackbarService);
  isPickingPage(){return this.router.url.split('?')[0]==='/operations/picking';}

  themes: string[] = [];
  isSidebarCollapsed = true;
  public avatarUrl = '';
  public loginUser$ = this.#authService.user();
  public appName = 'Supidoo Control';

  constructor() {
    this.themes = this.themeService.getThemes();
  }

  ngOnInit(): void {
    const user = this.loginUser$;

    if (user?.name) {
      this.avatarUrl = this.avatarService.getAvatarDataUrl(user.name, 40);
    }
  }

  changeTheme(theme: string) {
    this.themeService.setTheme(theme);
  }

  toggleSidebar() {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  navigateToOrder() {
    this.router.navigate(['/orders']); // Assuming a general orders page
  }

  async logout() {
    await this.#authService.logout();
    this.snackbar.success('Logged out successfully');
    this.router.navigate(['/login']);
  }
}
